/**
 * Linear interpolation (lerp) function
 * Maps a value from an input range to an output range
 *
 * @param inputRange - [min, max] of the input values (e.g., [0, 100] for percentages)
 * @param outputRange - [min, max] of the output values (e.g., [2, 500] for pixels)
 * @param inputValue - The value to interpolate
 * @returns The interpolated output value
 *
 * @example
 * // Map 50% to a pixel value between 2px and 500px
 * lerp([0, 100], [2, 500], 50) // returns 251
 *
 * // Map 0% - still returns minimum output (2px)
 * lerp([0, 100], [2, 500], 0) // returns 2
 */
export const lerp = (
  inputRange: [number, number],
  outputRange: [number, number],
  inputValue: number
): number => {
  const [inputMin, inputMax] = inputRange;
  const [outputMin, outputMax] = outputRange;

  // Handle edge case where input range is zero (avoid division by zero)
  if (inputMax === inputMin) {
    return outputMin;
  }

  // Clamp input value to input range
  const clampedInput = Math.max(inputMin, Math.min(inputMax, inputValue));

  // Calculate the normalized position (0 to 1) within input range
  const normalizedPosition = (clampedInput - inputMin) / (inputMax - inputMin);

  // Map to output range
  const output = outputMin + normalizedPosition * (outputMax - outputMin);

  return output;
};
